// Whether the height-locked "watch bento" fits — JS-owned because the answer
// depends on which CHROME is on screen, which a media query cannot see: a
// browser window spends ~240px on app chrome (brand bar, nav rail, toolbar)
// that the kiosk removes, so the same 768-tall display that cannot hold the
// grid in a browser holds it comfortably fullscreen. The `nm-bento` class on
// <body> is the single gate every bento rule requires; the media query under
// it pins only landscape, the width floor, and the kiosk height floor.

import { createContext, useEffect, useState } from 'react';

import { overflows } from './useFittedDensity';
import { ellipsized } from './useHeaderYields';

export const BENTO_MIN_WIDTH = 1024;
/** Browser windows need the headroom the app chrome consumes. */
export const BENTO_BROWSER_MIN_HEIGHT = 840;
/** The app chrome the height thresholds were tuned against (brand bar 72 +
 *  rail 60). */
const TUNED_CHROME_PX = 132;
/** Kiosk drops the chrome, so the same grid fits far shorter displays. */
export const BENTO_KIOSK_MIN_HEIGHT = 680;

export interface BentoViewport {
  width: number;
  height: number;
  /** Fullscreen kiosk active (`nm-kiosk`) — the chrome is off screen. */
  kiosk: boolean;
  /**
   * False when the composed modules cannot all be placed in the fixed grid —
   * e.g. a stereo with no vacated slot would spawn a fifth row inside a
   * hidden-overflow shell, clipping panels silently. The bento's promise is
   * "everything on one screen"; a composition it cannot keep that promise
   * for gets the scrolling layout instead.
   */
  compositionFits?: boolean;
}

/** Pure predicate — the one place the thresholds live. Landscape follows the
 *  CSS media definition (width ≥ height). */
export function bentoViable({
  width,
  height,
  kiosk,
  compositionFits = true,
}: BentoViewport): boolean {
  if (!compositionFits) return false;
  if (width < height) return false;
  if (width < BENTO_MIN_WIDTH) return false;
  return height >= (kiosk ? BENTO_KIOSK_MIN_HEIGHT : BENTO_BROWSER_MIN_HEIGHT);
}

/**
 * The width the grid gets in the shell's main column. Edit with Neo's column
 * makes room by padding the page's right side past its left (neo-panel.css);
 * that room is the panel's, so it comes off the width. With the panel shut the
 * padding is even and the width is the main column's, as before (TOM-395).
 */
export function bentoColumnWidth(
  mainWidth: number,
  paddingLeft: number,
  paddingRight: number
): number {
  return mainWidth - Math.max(0, paddingRight - paddingLeft);
}

/** How far a cell's content may run past its box before it counts as clipped:
 *  sub-pixel rounding and a hairline, as anchor-rendered's invariant allows. */
export const CLIP_TOLERANCE_PX = 4;

/** Checks a settling grid gets, about a frame each, before a cell that is
 *  still tightening counts as one that can't fit. */
export const SETTLE_CHECKS = 30;

export interface MeasuredBox {
  scrollHeight: number;
  clientHeight: number;
  scrollWidth: number;
  clientWidth: number;
}

/**
 * A row list in a cell that takes a density tier (`data-density`): the Tanks
 * card's, or the stereo's zones. Such a list holds its own overflow, so a row it
 * cuts off never shows in the cell's own box and is measured here.
 */
export interface DensityList {
  density: string | null;
  /** Fitted to its cell (`useFittedDensity`, `data-fitted`): it steps to a
   *  tighter tier by itself while it overflows. */
  fitted: boolean;
  scrollHeight: number;
  clientHeight: number;
}

export interface CellFit {
  /** The cell's content, or one of its lists, runs past its box. */
  clipped: boolean;
  /** A fitted list in it still overflows above its tightest tier, so it is
   *  about to step down and the cell may yet fit. */
  settling: boolean;
}

/**
 * Text in a cell cut where the cell's own box can't show it (TOM-450): none,
 * cut for good, or a module header's title cut beside chips that are still
 * giving way to it (useHeaderYields), which is about to fit.
 */
export type TextCut = 'none' | 'cut' | 'yielding';

/**
 * One cell of the bento, measured (TOM-396). `text` says whether text in it is
 * cut: an ellipsis, or a row run out of a scroll box inside the cell sideways.
 * A cell too narrow for its words clips as surely as one too short for its
 * rows (TOM-450).
 */
export function cellFit(
  cell: MeasuredBox,
  lists: readonly DensityList[],
  text: TextCut = 'none'
): CellFit {
  const stepping = (l: DensityList) => l.fitted && l.density !== 'tight';
  return {
    clipped:
      text !== 'none' ||
      cell.scrollHeight - cell.clientHeight > CLIP_TOLERANCE_PX ||
      cell.scrollWidth - cell.clientWidth > CLIP_TOLERANCE_PX ||
      lists.some((l) => overflows(l)),
    settling: text === 'yielding' || lists.some((l) => stepping(l) && overflows(l)),
  };
}

/**
 * Whether text in a cell is cut out of sight inside it: an element that
 * truncates with an ellipsis and is short of its text, or a scroll box that
 * runs its content out sideways or downwards. Charts' SVG and the map's own
 * canvas are not text the cell lays out. A cut in a header whose chips can
 * still give way is yielding: a resize measures the new width a frame before
 * the chips step down.
 *
 * Downwards is a module's body, which holds its own overflow in the bento: a
 * stale card's age line takes its room, and the body's last line runs under
 * it, out of sight (TOM-519). A density list holds its rows the same way, and
 * is measured with its tiers instead (`cellFit`'s lists).
 */
function textCut(cell: HTMLElement): TextCut {
  let yielding = false;
  const scrolls = (overflow: string) => overflow === 'auto' || overflow === 'scroll';
  for (const el of Array.from(cell.querySelectorAll<HTMLElement>('*'))) {
    if (el.closest('svg, .anchor-map__canvas') !== null) continue;
    // To the subpixel: an ellipsis is drawn for an overrun scrollWidth rounds
    // away.
    if (ellipsized(el)) {
      const head = el.closest('.overview-module-head');
      if (head?.querySelector('[data-head-yielding]') == null) return 'cut';
      yielding = true;
      continue;
    }
    const sideways = el.scrollWidth - el.clientWidth > CLIP_TOLERANCE_PX;
    const down =
      el.scrollHeight - el.clientHeight > CLIP_TOLERANCE_PX && !el.hasAttribute('data-density');
    if (!sideways && !down) continue;
    const css = getComputedStyle(el);
    if ((sideways && scrolls(css.overflowX)) || (down && scrolls(css.overflowY))) return 'cut';
  }
  return yielding ? 'yielding' : 'none';
}

/**
 * The bento's measured verdict: it fits, it is still settling (a fitted list
 * is stepping down), or a cell clips with nothing left to tighten. A clip
 * anywhere else is final even while another cell settles.
 */
export function bentoFitVerdict(cells: readonly CellFit[]): 'fits' | 'settling' | 'clips' {
  if (cells.some((c) => c.clipped && !c.settling)) return 'clips';
  if (cells.some((c) => c.settling)) return 'settling';
  return 'fits';
}

/**
 * The boat's home draws its board in a scroll area under a fixed header
 * (TOM-510): the bento there gets the area's width, and the height from the
 * board's top to the area's foot, not the window's. `scroll` is the area,
 * `board` the board's box inside it.
 */
export interface BentoArea {
  scroll: HTMLElement;
  board: HTMLElement;
}

/** Home's board area, for the bento it draws; null on every other page. */
export const BentoAreaContext = createContext<BentoArea | null>(null);

/**
 * The room a board has in home's area: its own width, and from its top to the
 * foot of the area's padding box. `boardTop` is measured from the top of the
 * area's content, so the room is the same however far the area is scrolled.
 */
export function bentoAreaRoom(
  boardTop: number,
  boardWidth: number,
  areaHeight: number,
  areaPaddingBottom: number
): { width: number; height: number } {
  return { width: boardWidth, height: areaHeight - areaPaddingBottom - boardTop };
}

/** The size a measured verdict holds for: a new one tries the bento again. */
export function bentoSizeKey(width: number, height: number, columnOpen: boolean): string {
  return `${Math.round(width)}x${Math.round(height)}${columnOpen ? '+neo' : ''}`;
}

/** Every bento cell on screen, measured. */
function measureCells(): CellFit[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      '.overview-grid > [data-module], .underway-grid > [data-module]'
    )
  )
    .filter((el) => el.offsetParent !== null)
    .map((el) =>
      cellFit(
        el,
        Array.from(el.querySelectorAll<HTMLElement>('[data-density]')).map((l) => ({
          density: l.getAttribute('data-density'),
          fitted: l.hasAttribute('data-fitted'),
          scrollHeight: l.scrollHeight,
          clientHeight: l.clientHeight,
        })),
        // At anchor's grid only, for now. Underway's depth cell can't hold its
        // numeral and title in a wide font, and its layout is to yield around a
        // depth figure that has a floor; that is its own change, not this one
        // (TOM-450).
        el.parentElement?.classList.contains('overview-grid') === true ? textCut(el) : 'none'
      )
    );
}

/**
 * Keep `body.nm-bento` in sync with the live viewport, kiosk state, the
 * composition and Edit with Neo's column. Owned by the page that draws the
 * bento; the class is removed on unmount so no other route inherits a stale
 * layout gate. Returns whether the bento is engaged. On home, `area` is the
 * board's room, measured in place of the window's (TOM-510).
 *
 * The thresholds are a forecast, and the measured fit is the check (TOM-396):
 * once the bento is engaged, every cell must hold its content. A cell that
 * clips with nothing left to tighten sends the page back to the scrolling
 * layout, and that verdict is kept for the size. The scrolling layout can't
 * clip a cell, so measuring it again would only flip the page back and forth.
 * A new window size, or Edit with Neo opening or shutting, tries again.
 */
export function useBentoViability(
  kiosk: boolean,
  compositionFits = true,
  area: BentoArea | null = null
): boolean {
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    // The sizes the bento failed its measured fit at, and the size it is
    // engaged at now (null while it is not).
    const clipsAt = new Set<string>();
    let engagedAt: string | null = null;
    let frame = 0;
    let unsettled = 0;

    const checkFit = () => {
      if (engagedAt === null) return;
      const verdict = bentoFitVerdict(measureCells());
      if (verdict === 'fits') {
        unsettled = 0;
        return;
      }
      unsettled += 1;
      if (verdict === 'clips' || unsettled > SETTLE_CHECKS) {
        clipsAt.add(engagedAt);
        engagedAt = null;
        unsettled = 0;
        document.body.classList.remove('nm-bento');
        setEngaged(false);
        return;
      }
      frame = requestAnimationFrame(checkFit);
    };
    const scheduleFit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(checkFit);
    };

    const compute = () => {
      if (area !== null && !kiosk) {
        // Home: the board's own room under the fixed header. The thresholds
        // are window heights with the tuned chrome on top, so it is added back.
        const scroll = area.scroll.getBoundingClientRect();
        const room = bentoAreaRoom(
          area.board.getBoundingClientRect().top - scroll.top + area.scroll.scrollTop,
          area.board.clientWidth,
          area.scroll.clientHeight,
          parseFloat(getComputedStyle(area.scroll).paddingBottom) || 0
        );
        engage(
          bentoViable({
            width: room.width,
            height: room.height + TUNED_CHROME_PX,
            kiosk,
            compositionFits,
          }),
          bentoSizeKey(room.width, room.height, false)
        );
        return;
      }
      // The grid's width is the content column's, not the window's: on desktop
      // the boat shell's sidebar takes its share (TOM-193), and Edit with Neo's
      // column takes its own (TOM-395). Kiosk hides the sidebar, and outside
      // the shell (the design preview) there is none.
      const main = kiosk ? null : document.querySelector<HTMLElement>('.boat-app__main');
      const shell = main?.querySelector<HTMLElement>('.neo-shell') ?? null;
      let width = main?.clientWidth ?? window.innerWidth;
      if (shell !== null) {
        const pad = getComputedStyle(shell);
        width = bentoColumnWidth(width, parseFloat(pad.paddingLeft), parseFloat(pad.paddingRight));
      }
      // The height thresholds were set for the old chrome: a 72px brand bar and
      // a 60px rail. The shell's chrome is now its header plus, on phone and
      // tablet, the floating tab bar's band (--header-h and --nav-rail-h on
      // .boat-app). Charge the difference to the window height so the grid gets
      // the room the thresholds were tuned for.
      let height = window.innerHeight;
      if (main !== null) {
        const css = getComputedStyle(main);
        const chrome =
          (parseFloat(css.getPropertyValue('--header-h')) || 0) +
          (parseFloat(css.getPropertyValue('--nav-rail-h')) || 0);
        height -= chrome - TUNED_CHROME_PX;
      }
      const key = bentoSizeKey(width, height, document.body.classList.contains('neo-column-open'));
      engage(bentoViable({ width, height, kiosk, compositionFits }), key);
    };
    function engage(forecast: boolean, key: string) {
      const viable = forecast && !clipsAt.has(key);
      document.body.classList.toggle('nm-bento', viable);
      setEngaged(viable);
      engagedAt = viable ? key : null;
      unsettled = 0;
      if (viable) scheduleFit();
    }
    compute();
    window.addEventListener('resize', compute);
    // Home's area changes size with the window, and the board's room with
    // what sits above it in the area (Neo's status, TOM-512).
    const resized =
      area !== null && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(compute) : null;
    if (area !== null) {
      resized?.observe(area.scroll);
      for (const child of Array.from(area.scroll.children)) {
        if (child !== area.board) resized?.observe(child);
      }
    }
    // Opening or closing Edit with Neo's column (`body.neo-column-open`)
    // changes the shell's padding, not the window. Only that class is watched:
    // the body's other classes, this gate's own among them, change no width.
    let columnOpen = document.body.classList.contains('neo-column-open');
    const observer = new MutationObserver(() => {
      const open = document.body.classList.contains('neo-column-open');
      if (open === columnOpen) return;
      columnOpen = open;
      compute();
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // New fonts change every cell's widths: what clipped may fit, and what fit
    // may clip, so the kept verdicts go and the bento is tried again (TOM-450).
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    const onFonts = () => {
      clipsAt.clear();
      compute();
    };
    fonts?.addEventListener('loadingdone', onFonts);
    // A cell's content can grow after the bento engaged (a forecast arrives, a
    // tank appears) or a fitted list can step its tier: measure again then.
    const content = new MutationObserver(() => {
      if (engagedAt !== null) scheduleFit();
    });
    content.observe((kiosk ? null : document.querySelector('.boat-app__main')) ?? document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-density'],
    });
    return () => {
      window.removeEventListener('resize', compute);
      fonts?.removeEventListener('loadingdone', onFonts);
      observer.disconnect();
      content.disconnect();
      resized?.disconnect();
      cancelAnimationFrame(frame);
      document.body.classList.remove('nm-bento');
    };
  }, [kiosk, compositionFits, area]);
  return engaged;
}
